import type { ContractSuretyAccountUnderwritingModelSize, SuretyAccountId } from "@inrev/common";
import type {
	AbandonPayload,
	StepCompletedPayload,
	StepCompletedWithErrorsPayload,
} from "@portal/components/multiform/types";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { z } from "zod";
import {
	type AccountSubmissionEntry,
	type AccountSubmissionSurface,
	trackAccountSubmissionFlowAbandoned,
	trackAccountSubmissionFlowCompleted,
	trackAccountSubmissionFlowEntered,
	trackAccountSubmissionFlowFailed,
	trackAccountSubmissionStepCompleted,
	trackAccountSubmissionStepCompletedWithErrors,
} from "../../../../analytics";
import { isApiError } from "../../../../utils/error";
import {
	getEmptyFormFieldPaths,
	getEmptyPartialFieldPaths,
	getNestedValue,
} from "../../../../utils/schema-fields";

type FlowVariant = "prequal" | "combined";

export const sanitizeError = (error: unknown): string => {
	if (isApiError(error)) {
		const detail = error.message ? `${error.name}: ${error.message}` : error.name;
		return `${error.statusCode}: ${detail}`;
	}
	if (error instanceof Error) {
		if (!error.message || error.message === error.name) {
			return error.name;
		}
		return `${error.name}: ${error.message}`;
	}
	return "Unknown error";
};

/**
 * A flow that never mounts a SectionNavLayout (admin quick submit) has no steps
 * to report, but still needs to be able to abandon.
 */
const SINGLE_PAGE_ABANDON_PAYLOAD: AbandonPayload = {
	lastStepName: "quick-submit",
	lastStepIndex: 0,
	furthestStepName: "quick-submit",
	furthestStepIndex: 0,
	emptyFieldPaths: [],
};

export const useAccountSubmissionAnalytics = <TFormData>(
	flowVariant: FlowVariant,
	schema: z.ZodType,
	entry: AccountSubmissionEntry,
) => {
	const flowStartedAtRef = useRef(DateTime.now());
	const hasFiredEnteredRef = useRef(false);
	const flowEndedRef = useRef(false);
	const abandonPayloadGetterRef = useRef<(() => AbandonPayload) | null>(null);
	const surfaceRef = useRef<AccountSubmissionSurface>(entry);

	const flowContext = useCallback(
		() => ({ flowVariant, entry, surface: surfaceRef.current }),
		[flowVariant, entry],
	);

	useEffect(() => {
		if (hasFiredEnteredRef.current) return;
		hasFiredEnteredRef.current = true;
		trackAccountSubmissionFlowEntered(flowContext());
	}, [flowContext]);

	const registerAbandonPayloadGetter = useCallback((getter: (() => AbandonPayload) | null) => {
		abandonPayloadGetterRef.current = getter;
	}, []);

	/**
	 * Called when an admin escalates a quick submit to the full multi-step form.
	 * `entry` keeps recording where the flow started; `surface` follows the user.
	 */
	const markEscalatedToFullForm = useCallback(() => {
		surfaceRef.current = "full";
	}, []);

	const onStepCompleted = useCallback(
		(payload: StepCompletedPayload) => {
			trackAccountSubmissionStepCompleted({ ...flowContext(), ...payload });
		},
		[flowContext],
	);

	const onStepCompletedWithErrors = useCallback(
		(payload: StepCompletedWithErrorsPayload) => {
			trackAccountSubmissionStepCompletedWithErrors({ ...flowContext(), ...payload });
		},
		[flowContext],
	);

	const onAbandon = useCallback(
		(payload: AbandonPayload) => {
			if (flowEndedRef.current) return;
			flowEndedRef.current = true;
			trackAccountSubmissionFlowAbandoned({
				...flowContext(),
				lastStepName: payload.lastStepName,
				lastStepIndex: payload.lastStepIndex,
				furthestStepName: payload.furthestStepName,
				furthestStepIndex: payload.furthestStepIndex,
				totalDurationMs: DateTime.now().diff(flowStartedAtRef.current).toMillis(),
				emptyFieldPaths: payload.emptyFieldPaths,
			});
		},
		[flowContext],
	);

	const onAccountCreated = useCallback(
		(
			accountId: SuretyAccountId,
			data: TFormData,
			accountVariant: ContractSuretyAccountUnderwritingModelSize,
		) => {
			if (flowEndedRef.current) return;
			flowEndedRef.current = true;
			const emptyFieldPaths = getEmptyFormFieldPaths(schema, data);
			const emptyPartialFieldPaths = getEmptyPartialFieldPaths(schema, data);
			const financialFiles = getNestedValue(data, "account.financials.files");
			const financialFilesUploadedCount = Array.isArray(financialFiles) ? financialFiles.length : 0;
			trackAccountSubmissionFlowCompleted({
				...flowContext(),
				accountId,
				accountVariant,
				totalDurationMs: DateTime.now().diff(flowStartedAtRef.current).toMillis(),
				isPartial: emptyPartialFieldPaths.length > 0,
				emptyFieldPaths,
				emptyPartialFieldPaths,
				financialFilesUploadedCount,
			});
		},
		[flowContext, schema],
	);

	/**
	 * Not terminal. A failed submit leaves the user on the form, so a subsequent
	 * retry can still report a completion.
	 */
	const onSubmissionFailed = useCallback(
		(error: unknown) => {
			trackAccountSubmissionFlowFailed({ ...flowContext(), errorMessage: sanitizeError(error) });
		},
		[flowContext],
	);

	const sectionNavCallbacks = useMemo(
		() => ({
			onStepCompleted,
			onStepCompletedWithErrors,
			onAbandon,
			registerAbandonPayloadGetter,
		}),
		[onStepCompleted, onStepCompletedWithErrors, onAbandon, registerAbandonPayloadGetter],
	);

	const abandonCurrentFlow = useCallback(() => {
		onAbandon(abandonPayloadGetterRef.current?.() ?? SINGLE_PAGE_ABANDON_PAYLOAD);
	}, [onAbandon]);

	const abandonCurrentFlowRef = useRef(abandonCurrentFlow);
	abandonCurrentFlowRef.current = abandonCurrentFlow;

	const pendingAbandonRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		// StrictMode's dev-only unmount/remount is synchronous, so a remount
		// always beats the deferred abandon queued by the cleanup below.
		if (pendingAbandonRef.current !== null) {
			clearTimeout(pendingAbandonRef.current);
			pendingAbandonRef.current = null;
		}
		const handlePageHide = (event: PageTransitionEvent) => {
			// page is going to bfcache; user may return with form state intact
			if (event.persisted) return;
			abandonCurrentFlowRef.current();
		};
		window.addEventListener("pagehide", handlePageHide);
		return () => {
			window.removeEventListener("pagehide", handlePageHide);
			// Covers closing a modal, popping a popover, and bouncing off an
			// untouched form -- the navigation blocker only fires when it is dirty.
			pendingAbandonRef.current = setTimeout(() => {
				pendingAbandonRef.current = null;
				abandonCurrentFlowRef.current();
			});
		};
	}, []);

	return {
		sectionNavCallbacks,
		markEscalatedToFullForm,
		onAccountCreated,
		onSubmissionFailed,
		abandonCurrentFlow,
	};
};

export type AccountSubmissionAnalytics<TFormData = unknown> = ReturnType<
	typeof useAccountSubmissionAnalytics<TFormData>
>;
